import { useState, useEffect } from 'react';
import { catalogApi, type CatalogItemDto } from '../../api/catalog';
import LoadingSpinner from '../../components/LoadingSpinner';
import MoneyCell from '../../components/MoneyCell';
import { useToast } from '../../components/Toast';
import { useIsMirror } from '../../hooks/useIsMirror';

export default function CatalogList() {
  const { addToast } = useToast();
  // ADR-004: mirror workspaces (mysql, asmx-owned) are READ-ONLY — hide the
  // write affordances; the API 403s them anyway (defense in depth).
  const isMirror = useIsMirror();
  const [items, setItems] = useState<CatalogItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editingItem, setEditingItem] = useState<CatalogItemDto | null>(null);

  const [form, setForm] = useState({
    name: '', unit: 'vnt.', unitPrice: 0, vatRate: 21,
    incomeAccount: 0, expenseAccount: 0, vatAccount: 0,
    type: 'service', description: '', code: ''
  });

  const loadItems = async () => {
    setLoading(true);
    try {
      const result = await catalogApi.list({ searchText: searchText || undefined, type: filterType || undefined });
      setItems(result);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { loadItems(); }, []);

  const handleSubmit = async () => {
    if (editingItem) {
      await catalogApi.update(editingItem.id, { ...form, isObsolete: false });
    } else {
      await catalogApi.create(form);
    }
    setShowAdd(false);
    setEditingItem(null);
    resetForm();
    loadItems();
  };

  const handleDelete = async (id: number) => {
    if (confirm('Ištrinti šį katalogo įrašą?')) {
      const item = items.find(i => i.id === id);
      await catalogApi.delete(id);
      addToast(`Katalogo įrašas „${item?.name || '#' + id}“ ištrintas`, 'success');
      loadItems();
    }
  };

  const resetForm = () => setForm({ name: '', unit: 'vnt.', unitPrice: 0, vatRate: 21, incomeAccount: 0, expenseAccount: 0, vatAccount: 0, type: 'service', description: '', code: '' });

  const startEdit = (item: CatalogItemDto) => {
    setEditingItem(item);
    setForm({ name: item.name, unit: item.unit, unitPrice: item.unitPrice, vatRate: item.vatRate, incomeAccount: item.incomeAccount, expenseAccount: item.expenseAccount, vatAccount: item.vatAccount, type: item.type, description: item.description || '', code: item.code || '' });
    setShowAdd(true);
  };

  return (
    <div className="space-y-4">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Paslaugų ir prekių katalogas</h1>
        {!isMirror && (
          <button onClick={() => { resetForm(); setEditingItem(null); setShowAdd(!showAdd); }}>
            {showAdd ? 'Atšaukti' : '+ Pridėti įrašą'}
          </button>
        )}
      </div>

      <div className="flex gap-3 items-end mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Paieška</label>
          <input className="px-2 py-1.5 text-sm border border-gray-300 rounded-md w-48" placeholder="Filtruoti pagal pavadinimą…" value={searchText} onChange={e => setSearchText(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadItems()} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Tipas</label>
          <select className="px-2 py-1.5 text-sm border border-gray-300 rounded-md" value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">Visi tipai</option>
            <option value="service">Paslaugos</option>
            <option value="goods">Prekės</option>
          </select>
        </div>
        <button className="px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 border border-primary-200" onClick={loadItems}>Ieškoti</button>
      </div>

      {showAdd && !isMirror && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3>{editingItem ? 'Redaguoti' : 'Pridėti'} katalogo įrašą</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
            <input placeholder="Pavadinimas *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            <input placeholder="Kodas" value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} />
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
              <option value="service">Paslauga</option>
              <option value="goods">Prekė</option>
            </select>
            <input placeholder="Vnt." value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} />
            <input type="number" placeholder="Kaina" value={form.unitPrice || ''} onChange={e => setForm({ ...form, unitPrice: parseFloat(e.target.value) || 0 })} />
            <input type="number" placeholder="PVM %" value={form.vatRate || ''} onChange={e => setForm({ ...form, vatRate: parseFloat(e.target.value) || 0 })} />
            <input type="number" placeholder="Pajamų sąskaita" value={form.incomeAccount || ''} onChange={e => setForm({ ...form, incomeAccount: parseInt(e.target.value) || 0 })} />
            <input type="number" placeholder="Sąnaudų sąskaita" value={form.expenseAccount || ''} onChange={e => setForm({ ...form, expenseAccount: parseInt(e.target.value) || 0 })} />
            <input type="number" placeholder="PVM sąskaita" value={form.vatAccount || ''} onChange={e => setForm({ ...form, vatAccount: parseInt(e.target.value) || 0 })} />
          </div>
          <textarea placeholder="Aprašymas" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ width: '100%', marginTop: '0.5rem' }} />
          <button onClick={handleSubmit} style={{ marginTop: '0.5rem' }}>
            {editingItem ? 'Atnaujinti' : 'Sukurti'}
          </button>
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Kodas</th>
              <th>Pavadinimas</th>
              <th>Tipas</th>
              <th>Vnt.</th>
              <th style={{ textAlign: 'right' }}>Kaina</th>
              <th style={{ textAlign: 'right' }}>PVM %</th>
              <th>Pajamų sąsk.</th>
              <th>Veiksmai</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id} style={item.isObsolete ? { opacity: 0.5 } : {}}>
                <td>{item.code || '-'}</td>
                <td>{item.name}</td>
                <td>{item.type === 'service' ? 'Paslauga' : 'Prekė'}</td>
                <td>{item.unit}</td>
                <td style={{ textAlign: 'right' }}><MoneyCell value={item.unitPrice} /></td>
                <td style={{ textAlign: 'right' }}>{item.vatRate}%</td>
                <td>{item.incomeAccount}</td>
                <td>
                  {!isMirror && (
                    <>
                      <button onClick={() => startEdit(item)}>Redaguoti</button>
                      <button onClick={() => handleDelete(item.id)} style={{ marginLeft: '0.5rem', color: 'red' }}>Trinti</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
